/**
 * Seam between a review-collection API and the local persisted store.
 *
 * - false (default, and the only working value today): the settings live
 *   entirely in `useReviewInviteStore`, no network calls. They survive a reload
 *   because the store persists; nothing is shared between devices or staff
 *   members, and nothing reaches the guest-facing app.
 * - true: `useReviewInviteQuery` would hydrate the store from the endpoint
 *   below and every save would mirror back. Components do not change between
 *   the two modes, which is the whole point of the seam. Same arrangement as
 *   the loyalty programme, the floor planner and the roster.
 *
 * WHY IT IS OFF: `menu_core_be` has no review domain. The pulled spec in
 * `src/api/core/api.json` carries 95 paths and not one of them mentions a
 * review, a rating or an opinion, and there is no such module in the backend's
 * `src/domain`. This is not "not wired yet", it is "does not exist", so the
 * page below configures a flow nothing can yet run.
 *
 * WHAT THE BACKEND WOULD NEED, in the order it becomes useful:
 *
 * 1. The settings themselves, one document per restaurant, addressed like the
 *    floor plan is: GET/PUT `/restaurants/{restaurantId}/review-invite`. The
 *    body is the whole document and PUT replaces it, which is the shape this
 *    store already has.
 *
 * 2. The guest side has to read them, and it is a different app: the invitation
 *    is shown by `menu_guest_mobile_FE` after payment, or by whatever the QR on
 *    the table card opens. That read has no session, so it needs a public route
 *    the way the mobile-app theme already has one
 *    (`/public/restaurants/{restaurantId}/mobile-app`). Without it the setting
 *    is saved here and never seen by the only party it describes.
 *
 * 3. Somewhere to put the stars a guest gives. They are the venue's private
 *    feedback, the feed this page lists, and until it exists the tiles on the
 *    right are the placeholder data they have always been.
 *
 * THE VENUE ON GOOGLE COMES FROM SETTINGS: the review link is the one the
 * venue's Google connection carries (`features/google-location`), so this page
 * picks nothing and stores no place of its own.
 *
 * EVERY GUEST GETS THE LINK: the stars do not decide who is sent to Google.
 * Routing only happy guests there is review gating, which Google's review
 * policy prohibits and which lands on the restaurant's listing, so there is
 * no threshold to set.
 */
export const REVIEW_INVITE_USE_API = false

/** Backend path, relative to `/restaurants/{restaurantId}`. */
export const REVIEW_INVITE_ENDPOINT = '/review-invite'

/**
 * Bumped when the persisted shape changes.
 *
 * 1: initial shape.
 * 2: `rules.googleReviewUrl` replaced by `rules.place`, the venue picked out
 *    of Google's own suggestions.
 * 3: `rules.mode` dropped. The star threshold says the same thing on its own.
 * 4: `rules` dropped: the threshold, because every guest gets the link, and
 *    the place, because the link comes from the Google connection in Settings.
 *    That connection lives on the server, which an upgrade in the browser
 *    cannot write to, so a venue that picked its place here connects it once
 *    in Settings. Only dev ever had the picker: `main` has no version of this
 *    store at all.
 *
 * A bumped version with no `migrate` makes zustand discard the whole payload,
 * which here would take the on/off switch with it, and there is no backend to
 * restore it from.
 */
export const REVIEW_INVITE_SCHEMA_VERSION = 4
